import { useState } from "react";
import { useWoStatuses } from "utils/woStatus";
import { Button, Modal, Spinner as BsSpinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";
import { convertSqlTimestampToDate } from "utils/UtilityFuncs";
import CustomerPicker from "./CustomerPicker";

/**
 * Line Item Report: total quantities for one style over a date range, by team.
 *
 * Requested 2026-10-08 ("total quantities for style mlb-350 from January to
 * May 2026 sorted by team name").
 *
 * The style is matched EXACTLY as typed, ignoring only case and surrounding
 * spaces (the user's call): "MLB-350" does not count "MLB 350" or "MLB350".
 * The server sends back the stored spellings it matched -- shown here, quoted,
 * so what was counted is visible.
 *
 * Totals always cover every matching line; only the line LIST is capped.
 */

// A date input wants the LOCAL calendar day, not the UTC one.
const localDay = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const initialFilters = () => {
  const today = new Date();
  return {
    style: "",
    dateFrom: `${today.getFullYear()}-01-01`,
    dateTo: localDay(today),
    dateBy: "order",
    customer: null, // { id, cid, name } from CustomerPicker
    locationId: "",
    woStatus: "",
    color: "",
  };
};

const fmt = (n) => Number(n || 0).toLocaleString("en-US");

/*   Click-to-sort
 ********************************************* */

// Blanks always sort last, whichever way the column is sorted. Numbers and
// digit runs compare as numbers ("W-900" before "W-1200"), text ignores case.
const compare = (a, b, dir) => {
  const blankA = a === null || a === undefined || String(a).trim() === "";
  const blankB = b === null || b === undefined || String(b).trim() === "";
  if (blankA || blankB) return blankA === blankB ? 0 : blankA ? 1 : -1;
  const r =
    typeof a === "number" && typeof b === "number"
      ? a - b
      : String(a).trim().localeCompare(String(b).trim(), undefined, { numeric: true, sensitivity: "base" });
  return dir === "desc" ? -r : r;
};

// No key = the order the server sent (by team name).
const sortRows = (rows, sort) =>
  sort.key ? [...rows].sort((x, y) => compare(x[sort.key], y[sort.key], sort.dir)) : rows;

// First click sorts ascending, the next flips it.
const toggle = (field) => (s) => ({
  key: field,
  dir: s.key === field && s.dir === "asc" ? "desc" : "asc",
});

const SortTh = ({ label, field, sort, onSort, end = false }) => {
  const active = sort.key === field;
  return (
    <th
      className={end ? "text-end" : undefined}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className="btn btn-link p-0 fw-bold text-reset text-decoration-none text-nowrap"
        onClick={() => onSort(toggle(field))}
      >
        {label}
        <i
          className={`mdi ms-1 ${
            active ? (sort.dir === "asc" ? "mdi-arrow-up" : "mdi-arrow-down") : "mdi-swap-vertical text-muted"
          }`}
        ></i>
      </button>
    </th>
  );
};

export default function LineItemReport({ show, onHide, locations = [] }) {
  // Settings -> WO Statuses (user, 2026-10-29).
  const woStatuses = useWoStatuses();
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(initialFilters);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("lines");
  const [lineSort, setLineSort] = useState({ key: null, dir: "asc" });
  const [teamSort, setTeamSort] = useState({ key: null, dir: "asc" });

  const set = (field) => (e) => setFilters((f) => ({ ...f, [field]: e.target.value }));

  const run = async (e) => {
    e?.preventDefault();
    if (!filters.style.trim()) return;
    try {
      setLoading(true);
      const { customer, ...rest } = filters;
      // The picked customer goes as its id (customers.id), never its name.
      const body = { ...rest, style: filters.style.trim(), customerId: customer?.id };
      // Leave empty optional filters out rather than sending "".
      for (const k of ["customerId", "locationId", "woStatus", "color", "dateFrom", "dateTo"]) {
        if (!body[k]) delete body[k];
      }
      const { data } = await API.post("/reports/line-items", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setResult({ ...data.data, ran: body });
      } else {
        dispatch(setNotification({ message: data?.message || "Could not run the report", type: "error" }));
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = () => {
    if (!result) return;
    const { ran, totals, byTeam, lines } = result;

    const lineRows = lines.map((l) => ({
      "W-ID": `W-${l.woId}`,
      "Order Date": l.WODate ? convertSqlTimestampToDate(l.WODate, true) : "",
      "Due Date": l.WoDueDate ? convertSqlTimestampToDate(l.WoDueDate, true) : "",
      Customer: l.customerName || "",
      "Job Name": l.jobName || "",
      Team: l.teamName || "",
      Style: l.productStyle || "",
      Color: l.productColor || "",
      Description: l.productDescription || "",
      Qty: l.totalQuantity || 0,
      Status: l.WOStatus || "",
    }));

    const teamRows = byTeam.map((t) => ({
      Team: t.team,
      "Total Qty": t.totalQty,
      "Line Items": t.lineItems,
      "Work Orders": t.workOrders,
    }));
    teamRows.push({
      Team: "TOTAL",
      "Total Qty": totals.totalQty,
      "Line Items": totals.lineItems,
      "Work Orders": totals.workOrders,
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lineRows), "Line items");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(teamRows), "By Team Summary");
    const safe = (s) => String(s || "").replace(/[^A-Za-z0-9-]+/g, "_");
    XLSX.writeFile(
      wb,
      `LineItems_${safe(ran.style)}_${ran.dateFrom || "start"}_to_${ran.dateTo || "now"}.xlsx`,
    );
  };

  return (
    <>
      <style>
        {`
          .line-item-report-modal {
            /* Width from the margins, not 100vw arithmetic -- vw includes the
               scrollbar (same approach as the Nova Shipping details modal). */
            max-width: none;
            width: auto;
            margin-left: 1.5rem;
            margin-right: 1.5rem;
          }
          /* The sidebar is fixed at z-index 99999 (NavBar.js); Bootstrap's
             modal (1055) and backdrop (1050) sit under it, which hid this
             modal's left edge. Same ladder as the Nova Shipping modal. */
          .line-item-report-backdrop { z-index: 100000; }
          .line-item-report          { z-index: 100001; }
        `}
      </style>
      <Modal
        show={show}
        onHide={onHide}
        className="line-item-report"
        backdropClassName="line-item-report-backdrop"
        dialogClassName="line-item-report-modal"
        centered
        // Not `scrollable`: a scrolling body clips the customer dropdown's
        // list. Long results scroll the whole modal instead.
      >
        <Modal.Header closeButton>
          <Modal.Title>Line Item Report</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/*   Filters
           ********************************************* */}
          <form onSubmit={run} className="row g-2 align-items-end mb-3">
            <div className="col-md-3">
              <label className="form-label mb-0">Style *</label>
              <input
                className="form-control"
                autoFocus
                value={filters.style}
                onChange={set("style")}
                placeholder="e.g. MLB-350"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-0">From</label>
              <input type="date" className="form-control" value={filters.dateFrom} onChange={set("dateFrom")} />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-0">To</label>
              <input type="date" className="form-control" value={filters.dateTo} onChange={set("dateTo")} />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-0">Date by</label>
              <select className="form-select" value={filters.dateBy} onChange={set("dateBy")}>
                <option value="order">Order date</option>
                <option value="due">Due date</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-0">Customer</label>
              <CustomerPicker
                value={filters.customer}
                onChange={(customer) => setFilters((f) => ({ ...f, customer }))}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-0">Location</label>
              <select className="form-select" value={filters.locationId} onChange={set("locationId")}>
                <option value="">All</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.locationName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label mb-0">WO Status</label>
              <select className="form-select" value={filters.woStatus} onChange={set("woStatus")}>
                <option value="">All</option>
                {woStatuses.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label mb-0">Color</label>
              <input className="form-control" value={filters.color} onChange={set("color")} placeholder="any" />
            </div>
            <div className="col-md-3 d-flex gap-2">
              <Button type="submit" variant="primary" disabled={loading || !filters.style.trim()}>
                {loading ? <BsSpinner animation="border" size="sm" /> : "Run Report"}
              </Button>
              <Button variant="success" onClick={exportExcel} disabled={!result || loading}>
                <i className="mdi mdi-file-excel"></i> Export Excel
              </Button>
            </div>
          </form>

          {/*   Results
           ********************************************* */}
          {result && (
            <>
              <div className="d-flex flex-wrap align-items-baseline gap-3 mb-2">
                <h5 className="mb-0">{fmt(result.totals.totalQty)} total</h5>
                <span className="text-muted">
                  {fmt(result.totals.lineItems)} line items · {fmt(result.totals.workOrders)} work orders
                </span>
              </div>

              {result.matchedStyles.length > 0 ? (
                <div className="mb-2 small">
                  <span className="text-muted me-1">Matched spellings:</span>
                  {result.matchedStyles.map((s) => (
                    <span key={s.style} className="badge bg-light text-dark border me-1" title={`${s.lineItems} line items`}>
                      {/* Quoted, so a trailing space is visible. */}
                      {JSON.stringify(s.style)} · {fmt(s.totalQty)}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="alert alert-info py-2">No line items match that style in this range.</div>
              )}

              {result.truncated && (
                <div className="alert alert-warning py-2">
                  Showing the first {fmt(result.lineCap)} of {fmt(result.totals.lineItems)} line items.
                  The totals above include all of them. Narrow the dates to list everything.
                </div>
              )}

              {result.matchedStyles.length > 0 && (
                <>
                  <ul className="nav nav-tabs mb-2">
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${view === "lines" ? "active" : ""}`} onClick={() => setView("lines")}>
                        Line items ({result.lines.length})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${view === "team" ? "active" : ""}`} onClick={() => setView("team")}>
                        By Team Summary ({result.byTeam.length})
                      </button>
                    </li>
                  </ul>

                  {view === "lines" ? (
                    <table className="table table-sm table-striped align-middle">
                      <thead>
                        <tr>
                          <SortTh label="W-ID" field="woId" sort={lineSort} onSort={setLineSort} />
                          <SortTh label="Order Date" field="WODate" sort={lineSort} onSort={setLineSort} />
                          <SortTh label="Customer" field="customerName" sort={lineSort} onSort={setLineSort} />
                          <SortTh label="Team" field="teamName" sort={lineSort} onSort={setLineSort} />
                          <SortTh label="Style" field="productStyle" sort={lineSort} onSort={setLineSort} />
                          <SortTh label="Color" field="productColor" sort={lineSort} onSort={setLineSort} />
                          <SortTh label="Description" field="productDescription" sort={lineSort} onSort={setLineSort} />
                          <SortTh label="Qty" field="totalQuantity" sort={lineSort} onSort={setLineSort} end />
                        </tr>
                      </thead>
                      <tbody>
                        {/* Unsorted, this is the server's order: by team name. */}
                        {sortRows(result.lines, lineSort).map((l) => (
                          <tr key={l.id}>
                            <td>
                              {/* A new tab: following the link in this modal
                                  would throw the report away. */}
                              <a href={`/work-order/details/all/${l.woId}?page=1`} target="_blank" rel="noreferrer">
                                W-{l.woId}
                              </a>
                            </td>
                            <td className="text-nowrap">
                              {l.WODate ? convertSqlTimestampToDate(l.WODate, true) : "-"}
                            </td>
                            <td>{l.customerName || "-"}</td>
                            <td>{l.teamName?.trim() || "-"}</td>
                            <td>{l.productStyle || "-"}</td>
                            <td>{l.productColor || "-"}</td>
                            <td>{l.productDescription || "-"}</td>
                            <td className="text-end">{fmt(l.totalQuantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table className="table table-sm table-striped align-middle">
                      <thead>
                        <tr>
                          <SortTh label="Team" field="team" sort={teamSort} onSort={setTeamSort} />
                          <SortTh label="Total Qty" field="totalQty" sort={teamSort} onSort={setTeamSort} end />
                          <SortTh label="Line Items" field="lineItems" sort={teamSort} onSort={setTeamSort} end />
                          <SortTh label="Work Orders" field="workOrders" sort={teamSort} onSort={setTeamSort} end />
                        </tr>
                      </thead>
                      <tbody>
                        {sortRows(result.byTeam, teamSort).map((t) => (
                          <tr key={t.team}>
                            <td>{t.team}</td>
                            <td className="text-end">{fmt(t.totalQty)}</td>
                            <td className="text-end">{fmt(t.lineItems)}</td>
                            <td className="text-end">{fmt(t.workOrders)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="fw-bold">
                          <td>Total</td>
                          <td className="text-end">{fmt(result.totals.totalQty)}</td>
                          <td className="text-end">{fmt(result.totals.lineItems)}</td>
                          <td className="text-end">{fmt(result.totals.workOrders)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
